/**
 * Player profile: index progression, scoring stats and event history.
 *
 * The graph is inline SVG rather than a chart library — one dependency fewer and
 * it renders identically on a clubhouse TV and a phone.
 */
import { useParams } from 'react-router-dom';
import { AdProvider } from '../lib/adClient.tsx';
import { AdZone } from '../components/AdZone.tsx';
import { ErrorNote, Section, Spinner } from '../components/Shell.tsx';
import { useFetch } from '../lib/useLive.ts';

interface Profile {
  player: {
    id: string;
    displayName: string;
    countryCode: string;
    playingAbilityIndex: number | null;
    lowIndex: number | null;
    handicapBodyIndex: number | null;
  };
  indexHistory: Array<{ effectiveOn: string; indexValue: string; softCapApplied: boolean; hardCapApplied: boolean }>;
  recentCards: Array<{ recordId: string; playedOn: string; adjustedGross: number; differential: string }>;
}

interface Stats {
  holesRecorded: number;
  scoringAverageToPar: number | null;
  byPar: Record<'par3' | 'par4' | 'par5', { holes: number; averageToPar: number | null }>;
  greensInRegulationPercent: number | null;
  fairwaysHitPercent: number | null;
  puttsPerHole: number | null;
  penaltiesPerRound: number | null;
  scoringSpread: { eagles: number; birdies: number; pars: number; bogeys: number; doublePlus: number };
  message?: string;
}

export function PlayerProfilePage() {
  const { playerId } = useParams<{ playerId: string }>();
  const profile = useFetch<Profile>(playerId ? `/api/players/${playerId}` : null);
  const stats = useFetch<Stats>(playerId ? `/api/players/${playerId}/stats` : null);
  const history = useFetch<{ history: Array<{ entryId: string; tournamentName: string; tournamentSlug: string; startsOn: string | null; entryStatus: string }> }>(
    playerId ? `/api/players/${playerId}/history` : null,
  );

  if (profile.loading) return <Spinner label="Loading profile" />;
  if (profile.error || !profile.data) {
    return (
      <Section title="Player profile">
        <ErrorNote message={profile.error ?? 'Player not found'} hint="Player profiles come from live event data, which needs a database." />
      </Section>
    );
  }

  const { player, indexHistory, recentCards } = profile.data;

  return (
    <AdProvider surface="profile" allowInterstitial={false}>
      <Section eyebrow="Player" title={player.displayName}>
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="space-y-6">
            <div className="grid gap-3 sm:grid-cols-4">
              <Stat label="Playing Ability" value={player.playingAbilityIndex?.toFixed(1) ?? '—'} accent />
              <Stat label="Low index (12 mo)" value={player.lowIndex?.toFixed(1) ?? '—'} />
              <Stat label="Union index" value={player.handicapBodyIndex?.toFixed(1) ?? 'not synced'} />
              <Stat label="Holes recorded" value={String(stats.data?.holesRecorded ?? 0)} />
            </div>

            {indexHistory.length > 1 ? (
              <div className="card p-5">
                <h3 className="board-title mb-1 text-lg font-bold">Index progression</h3>
                <p className="mb-4 text-xs text-[var(--color-muted-dim)]">
                  Lower is better. Our own Playing Ability index, not an official national handicap.
                </p>
                <IndexChart points={indexHistory.map((h) => ({ date: h.effectiveOn, value: Number(h.indexValue) }))} />
              </div>
            ) : null}

            {stats.data && stats.data.holesRecorded > 0 ? (
              <div className="card p-5">
                <h3 className="board-title mb-4 text-lg font-bold">Scoring</h3>
                <div className="grid gap-3 sm:grid-cols-3">
                  <Stat label="Average to par" value={formatToPar(stats.data.scoringAverageToPar)} />
                  <Stat label="Greens in regulation" value={stats.data.greensInRegulationPercent === null ? '—' : `${stats.data.greensInRegulationPercent}%`} />
                  <Stat label="Fairways hit" value={stats.data.fairwaysHitPercent === null ? '—' : `${stats.data.fairwaysHitPercent}%`} />
                  <Stat label="Putts per hole" value={stats.data.puttsPerHole?.toFixed(2) ?? '—'} />
                  <Stat label="Penalties per round" value={stats.data.penaltiesPerRound?.toFixed(1) ?? '—'} />
                  <Stat label="Par 3 / 4 / 5" value={`${formatToPar(stats.data.byPar.par3.averageToPar)} / ${formatToPar(stats.data.byPar.par4.averageToPar)} / ${formatToPar(stats.data.byPar.par5.averageToPar)}`} />
                </div>

                <h4 className="eyebrow mt-6 mb-2">Distribution</h4>
                <div className="flex h-8 overflow-hidden rounded-lg">
                  {(
                    [
                      ['eagles', 'var(--color-gold-500)'],
                      ['birdies', 'var(--color-fairway-300)'],
                      ['pars', 'var(--color-fairway-400)'],
                      ['bogeys', 'var(--color-sky-500)'],
                      ['doublePlus', 'var(--color-rose-sun)'],
                    ] as const
                  ).map(([key, colour]) => {
                    const total = Object.values(stats.data!.scoringSpread).reduce((s, v) => s + v, 0) || 1;
                    const value = stats.data!.scoringSpread[key];
                    const percent = (value / total) * 100;
                    if (percent < 0.5) return null;
                    return (
                      <div
                        key={key}
                        style={{ width: `${percent}%`, background: colour }}
                        title={`${key}: ${value} (${percent.toFixed(1)}%)`}
                        className="grid place-items-center text-[10px] font-bold text-[var(--color-fairway-950)]"
                      >
                        {percent > 8 ? value : ''}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[var(--color-muted)]">
                  <span>Eagles {stats.data.scoringSpread.eagles}</span>
                  <span>Birdies {stats.data.scoringSpread.birdies}</span>
                  <span>Pars {stats.data.scoringSpread.pars}</span>
                  <span>Bogeys {stats.data.scoringSpread.bogeys}</span>
                  <span>Doubles+ {stats.data.scoringSpread.doublePlus}</span>
                </div>
              </div>
            ) : (
              <div className="card p-5 text-sm text-[var(--color-muted)]">
                {stats.data?.message ?? 'No hole-by-hole scores recorded yet.'}
              </div>
            )}

            {recentCards.length ? (
              <div className="card p-5">
                <h3 className="board-title mb-3 text-lg font-bold">Counting cards</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_20%,transparent)] text-left text-[var(--color-muted)]">
                      <th className="py-2">Date</th>
                      <th className="py-2 text-right">Adjusted gross</th>
                      <th className="py-2 text-right">Differential</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentCards.map((card) => (
                      <tr key={card.recordId} className="border-b border-[color-mix(in_oklab,var(--color-fairway-400)_10%,transparent)]">
                        <td className="py-2">{card.playedOn}</td>
                        <td className="tabular py-2 text-right">{card.adjustedGross}</td>
                        <td className="tabular py-2 text-right text-[var(--color-fairway-300)]">{card.differential}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {history.data?.history.length ? (
              <div className="card p-5">
                <h3 className="board-title mb-3 text-lg font-bold">Events</h3>
                <ul className="space-y-2 text-sm">
                  {history.data.history.map((event) => (
                    <li key={event.entryId} className="flex items-center justify-between">
                      <span>{event.tournamentName}</span>
                      <span className="text-[11px] uppercase tracking-wider text-[var(--color-muted-dim)]">
                        {event.startsOn ?? ''} · {event.entryStatus}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          <aside className="space-y-4">
            <AdZone zoneKey="sidebar_primary" />
            <AdZone zoneKey="affiliate_slot" />
          </aside>
        </div>
      </Section>
    </AdProvider>
  );
}

const formatToPar = (value: number | null): string => (value === null ? '—' : value > 0 ? `+${value.toFixed(2)}` : value.toFixed(2));

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="card p-4">
      <p className="eyebrow">{label}</p>
      <p className={`tabular mt-1 text-xl font-bold ${accent ? 'text-[var(--color-gold-400)]' : 'text-[var(--color-chalk)]'}`}>{value}</p>
    </div>
  );
}

/** Sparkline-style index chart. Y axis is inverted: a lower index sits higher. */
function IndexChart({ points }: { points: Array<{ date: string; value: number }> }) {
  const width = 640;
  const height = 180;
  const pad = { top: 12, right: 12, bottom: 24, left: 34 };
  const values = points.map((p) => p.value);
  const min = Math.floor(Math.min(...values) - 1);
  const max = Math.ceil(Math.max(...values) + 1);
  const x = (i: number) => pad.left + (i / Math.max(1, points.length - 1)) * (width - pad.left - pad.right);
  const y = (value: number) => pad.top + ((value - min) / Math.max(0.1, max - min)) * (height - pad.top - pad.bottom);

  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const area = `${path} L${x(points.length - 1).toFixed(1)},${height - pad.bottom} L${x(0).toFixed(1)},${height - pad.bottom} Z`;

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" role="img" aria-label="Playing Ability index over time">
      <defs>
        <linearGradient id="indexFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--color-fairway-300)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--color-fairway-300)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[min, (min + max) / 2, max].map((tick) => (
        <g key={tick}>
          <line x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} stroke="var(--color-fairway-700)" strokeWidth="1" />
          <text x={pad.left - 6} y={y(tick) + 4} textAnchor="end" fontSize="10" fill="var(--color-muted-dim)">
            {tick.toFixed(1)}
          </text>
        </g>
      ))}
      <path d={area} fill="url(#indexFill)" />
      <path d={path} fill="none" stroke="var(--color-fairway-300)" strokeWidth="2.5" strokeLinejoin="round" />
      {points.map((point, i) => (
        <circle key={point.date} cx={x(i)} cy={y(point.value)} r="3" fill="var(--color-fairway-300)">
          <title>
            {point.date}: {point.value.toFixed(1)}
          </title>
        </circle>
      ))}
      <text x={pad.left} y={height - 6} fontSize="10" fill="var(--color-muted-dim)">
        {points[0]?.date}
      </text>
      <text x={width - pad.right} y={height - 6} fontSize="10" textAnchor="end" fill="var(--color-muted-dim)">
        {points[points.length - 1]?.date}
      </text>
    </svg>
  );
}
